import { useCallback } from 'react';
import { logger } from '@/services/LoggingService';
import { WealthicaStorage } from '../utils/storage';
import { WealthicaConnection } from '../types';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';

/**
 * Hook for managing Wealthica token and connection data in storage
 * All storage logic is centralized here for maintainability and testability.
 * 
 * IMPORTANT: This hook now checks both localStorage and sessionStorage for backward compatibility
 * and to ensure a seamless experience for users with existing connections.
 */
export function useTokenStorage() {
  // Get bearer token
  const getBearerToken = useCallback((): string | null => {
    // Server-only tokens: read from in-memory multi-store, not storage
    const activeId = useMultiWealthica2Store.getState().activeConnectionId;
    const token = activeId ? useMultiWealthica2Store.getState().getConnection(activeId)?.bearerToken : null;
    if (typeof token === 'string' && token.length > 0) {
      logger.info('useTokenStorage.getBearerToken', 'Using token from multi-store connection state', {
        tokenLength: token.length,
        tokenPrefix: token.substring(0, 10) + '...',
        source: 'multiWealthica2Store'
      });
      return token;
    }
    logger.warn('useTokenStorage.getBearerToken', 'No token found in multi-store');
    return null;
  }, []);

  // Set bearer token
  const setBearerToken = useCallback((token: string | null) => {
    // Server-only tokens: no-op for frontend persistence
    logger.info('useTokenStorage.setBearerToken', 'noop (server-only tokens)', {
      hasToken: !!token
    });
  }, []);

  // Get connection data
  const getConnectionData = useCallback((): WealthicaConnection | null => {
    const data = WealthicaStorage.getConnectionData();
    if (data && typeof data === 'object' && 'institutionId' in data && 'institutionIdentifier' in data && 'connectionId' in data && 'providerName' in data && 'timestamp' in data && 'state' in data) {
      logger.info('useTokenStorage.getConnectionData', 'Found valid connection data in WealthicaStorage', { data });
      return data as WealthicaConnection;
    }
    logger.warn('useTokenStorage.getConnectionData', 'No valid connection data found in WealthicaStorage');
    return null;
  }, []);

  // Set connection data
  const setConnectionData = useCallback((data: WealthicaConnection | null) => {
    if (data) {
      WealthicaStorage.setConnectionData(data);
      logger.info('useTokenStorage.setConnectionData', 'Successfully stored connection data', { data });
    } else {
      WealthicaStorage.setConnectionData(null);
      logger.info('useTokenStorage.setConnectionData', 'Cleared connection data');
    }
  }, []);

  // Get connection confirmed flag
  const getConnectionConfirmed = useCallback((): boolean => {
    return WealthicaStorage.getConnectionConfirmed();
  }, []);

  // Set connection confirmed flag
  const setConnectionConfirmed = useCallback((flag: boolean) => {
    WealthicaStorage.setConnectionConfirmed(flag);
  }, []);

  // Clear for user-initiated disconnect (respects persistence preferences)
  const clearForDisconnect = useCallback(() => {
    WealthicaStorage.clearForLogout();
    logger.info('useTokenStorage.clearForDisconnect', 'Cleared storage for user disconnect');
  }, []);

  return {
    getBearerToken,
    setBearerToken,
    getConnectionData,
    setConnectionData,
    getConnectionConfirmed,
    setConnectionConfirmed,
    clearForDisconnect,
  };
}